import { AccountMRIRadarApi } from "api/account-mri-radar.api";
import {
  TMRIRadarNegotiatorRelationData,
  TMRIRadarNegotiatorRelationExtendedData,
} from "types/mri-radar.type";
import { keyBy } from "lodash";
import { IObservableArray, makeAutoObservable, observable } from "mobx";
import { AppStore } from "store/App.store";
import { getUserFullName } from "utils/account-user.utils";

const accountMRIRadarApi = new AccountMRIRadarApi();

export class AccountIntegrationMRIRadarNegotiatorsStore {
  private readonly root: AppStore;
  private readonly accountId: number;
  private readonly negotiatorsRelations: IObservableArray<
    TMRIRadarNegotiatorRelationData
  >;
  private defaultMriNegotiatorEmail: string | null;

  constructor(root: AppStore, accountId: number) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.root = root;
    this.accountId = accountId;
    this.negotiatorsRelations = observable.array<
      TMRIRadarNegotiatorRelationData
    >();
    this.defaultMriNegotiatorEmail = null;
  }

  get mriRadarDefaultNegotiatorEmail() {
    return this.defaultMriNegotiatorEmail;
  }

  public setDefaultMriNegotiatorEmail(mriNegotiatorEmail: string | null) {
    this.defaultMriNegotiatorEmail = mriNegotiatorEmail;
  }

  get negotiatorsRelationsMapByUserAccountId() {
    return keyBy(this.negotiatorsRelations, relation => relation.userAccountId);
  }

  get mriRadarNegotiatorsData(): TMRIRadarNegotiatorRelationExtendedData[] {
    const accountStore = this.root.userAccountsStore.userAccountsMap[
      this.accountId
    ];
    const accountUsers = accountStore.accountUsersStore.accountUsersArray;
    const relationsMap = this.negotiatorsRelationsMapByUserAccountId;

    return accountUsers.map(accountUser => ({
      userAccountId: accountUser.userAccount.id,
      mriNegotiatorEmail:
        relationsMap[accountUser.userAccount.id]?.mriNegotiatorEmail ?? null,
      userName: getUserFullName(
        accountUser.firstName,
        accountUser.lastName,
        accountUser.email
      )!,
    }));
  }

  public setNegotiatorsRelations(relations: TMRIRadarNegotiatorRelationData[]) {
    this.negotiatorsRelations.replace(relations);
  }

  public async fetchAccountMRIRadarNegotiatorsRelations() {
    const data = await accountMRIRadarApi.fetchAccountMRIRadarNegotiatorRelations(
      this.accountId
    );
    this.setNegotiatorsRelations(data);
  }

  public async updateAccountMRIRadarSyncedNegotiators(
    relations: TMRIRadarNegotiatorRelationData[]
  ) {
    await accountMRIRadarApi.updateAccountMRIRadarNegotiatorRelations(
      this.accountId,
      relations.map(relation => ({
        userAccountId: relation.userAccountId,
        mriNegotiatorEmail: relation.mriNegotiatorEmail,
      }))
    );
    await this.fetchAccountMRIRadarNegotiatorsRelations();
  }

  public async fetchMRIRadarDefaultNegotiator() {
    const data = await accountMRIRadarApi.fetchMRIRadarDefaultNegotiator(
      this.accountId
    );
    this.setDefaultMriNegotiatorEmail(data.mriNegotiatorEmail);
  }

  public async updateMRIRadarDefaultNegotiator(
    mriNegotiatorEmail: string | null
  ) {
    await accountMRIRadarApi.updateMRIRadarDefaultNegotiator(
      this.accountId,
      mriNegotiatorEmail
    );
    this.setDefaultMriNegotiatorEmail(mriNegotiatorEmail);
  }
}
